import React, { useState } from 'react';
import { Container, Form, Button, Alert, Spinner, Row, Col, Card } from 'react-bootstrap';
import Papa from 'papaparse';
import axios from 'axios';

const BulkUploader = () => {
  const [csvType, setCsvType] = useState('bins');
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // Export dropdown
  const [exportType, setExportType] = useState('inventory_costs');

  // Live inventory cost report
  const [inventoryReport, setInventoryReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [topLimit, setTopLimit] = useState(100);

  // Live TOTAL inventory cost (single number)
  const [totalInventoryCost, setTotalInventoryCost] = useState(null);

  // Live Quick Buy report
  const [quickBuyReport, setQuickBuyReport] = useState(null);

  // Live Pending Inventory Costs report
  const [pendingInventoryCostsReport, setPendingInventoryCostsReport] = useState(null);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  /* -------------------------
     Helpers
  ------------------------- */
  const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const money = (v) =>
    toNumber(v).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const clearReports = () => {
    setInventoryReport(null);
    setTotalInventoryCost(null);
    setQuickBuyReport(null);
    setPendingInventoryCostsReport(null);
  };

  const getAuthHeaders = () => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('accessToken');

    if (!token) {
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  /* -------------------------
     Import logic
  ------------------------- */
  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!csvFile) {
      setError('Please select a CSV file.');
      return;
    }

    setUploading(true);
    setError(null);
    setMessage(null);

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const response = await axios.post('/api/tools/bulk-upload', {
            type: csvType,
            data: results.data,
          });

          setMessage(response.data.message || 'Upload successful.');
        } catch (err) {
          setError(err.response?.data?.error || 'Upload failed.');
        } finally {
          setUploading(false);
        }
      },
      error: (err) => {
        setError(`CSV parsing failed: ${err.message}`);
        setUploading(false);
      },
    });
  };

  /* -------------------------
     Live Reports
  ------------------------- */
  const fetchInventoryCostReport = async (limit) => {
    try {
      setLoadingReport(true);
      clearReports();
      setError(null);
      setMessage(null);

      const res = await axios.get(`/api/reports/top-inventory-cost?limit=${limit}`);

      setInventoryReport(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load inventory cost report.');
    } finally {
      setLoadingReport(false);
    }
  };

  const fetchTotalInventoryCost = async () => {
    try {
      setLoadingReport(true);
      clearReports();
      setError(null);
      setMessage(null);

      const res = await axios.get('/api/reports/top-inventory-cost?limit=0');

      setTotalInventoryCost(res.data.total_inventory_cost);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load total inventory cost.');
    } finally {
      setLoadingReport(false);
    }
  };

  const fetchQuickBuyReport = async () => {
    try {
      setLoadingReport(true);
      clearReports();
      setError(null);
      setMessage(null);

      const res = await axios.get(
        '/api/quick-batch/report-summary',
        getAuthHeaders()
      );

      setQuickBuyReport(res.data);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to load Quick Buy value report.'
      );
    } finally {
      setLoadingReport(false);
    }
  };

  const fetchPendingInventoryCosts = async () => {
    try {
      setLoadingReport(true);
      clearReports();
      setError(null);
      setMessage(null);

      const res = await axios.get(
        '/api/reports/pending-inventory-costs',
        getAuthHeaders()
      );

      setPendingInventoryCostsReport(res.data);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to load Pending Inventory Costs report.'
      );
    } finally {
      setLoadingReport(false);
    }
  };

  const handleExport = async () => {
    // 🔴 Live: Top inventory cost table
    if (exportType === 'top_inventory_cost') {
      fetchInventoryCostReport(topLimit);
      return;
    }

    // 🔴 Live: TOTAL inventory cost only
    if (exportType === 'total_inventory_cost') {
      fetchTotalInventoryCost();
      return;
    }

    // 🟣 Live: Quick Buy value report
    if (exportType === 'quick_buy_value_report') {
      fetchQuickBuyReport();
      return;
    }

    // 🟡 Live: Pending Inventory Costs report
    if (exportType === 'pending_inventory_costs') {
      fetchPendingInventoryCosts();
      return;
    }

    // 🟢 CSV downloads
    const link = document.createElement('a');

    switch (exportType) {
      case 'inventory_costs':
        link.href = '/api/tools/export-costs';
        link.setAttribute('download', 'inventory_costs.csv');
        break;

      case 'annual_inventory':
        link.href = '/api/reports/annual-inventory';
        link.setAttribute('download', 'annual_inventory.csv');
        break;

      case 'quarterly_sales': {
        const params = new URLSearchParams();

        if (startDate) params.append('start', startDate);
        if (endDate) params.append('end', endDate);

        link.href = `/api/reports/quarterly-sales?${params.toString()}`;
        link.setAttribute('download', 'quarterly_sales.csv');
        break;
      }

      default:
        alert('Please select a valid export type.');
        return;
    }

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container className="mt-4">
      <h3>Bulk Tools</h3>

      {/* ================= IMPORT ================= */}
      <h5 className="mt-3">Import</h5>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Select value={csvType} onChange={(e) => setCsvType(e.target.value)}>
            <option value="bins">Bin Locations</option>
            <option value="conditions">eBay Conditions</option>
            <option value="costs">Product Costs</option>
          </Form.Select>
        </Col>

        <Col md={4}>
          <Form.Control type="file" accept=".csv" onChange={handleFileChange} />
        </Col>

        <Col md={3}>
          <Button onClick={handleUpload} disabled={uploading}>
            {uploading ? <Spinner size="sm" animation="border" /> : 'Upload'}
          </Button>
        </Col>

        <Col md={2}>
          <Button
            variant="secondary"
            href={
              csvType === 'conditions'
                ? 'https://thorns-public-files.s3.amazonaws.com/templates/bulk-ebay-conditions-template.csv'
                : 'https://thorns-public-files.s3.amazonaws.com/templates/bulk-bin-template-minimal.csv'
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            Download Template
          </Button>
        </Col>
      </Row>

      {message && <Alert variant="success">{message}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* ================= EXPORT ================= */}
      <h5 className="mt-4">Export</h5>

      <Row className="mb-3 align-items-end">
        <Col md={4}>
          <Form.Select
            value={exportType}
            onChange={(e) => {
              setExportType(e.target.value);
              clearReports();
              setError(null);
              setMessage(null);
            }}
          >
            <option value="inventory_costs">Inventory Costs (CSV)</option>
            <option value="annual_inventory">Annual Inventory Report (CSV)</option>
            <option value="quarterly_sales">Quarterly Sales Report (CSV)</option>
            <option value="top_inventory_cost">Top Inventory Cost (Live)</option>
            <option value="total_inventory_cost">Total Inventory Cost (Live)</option>
            <option value="quick_buy_value_report">Quick Buy Value Report (Live)</option>
            <option value="pending_inventory_costs">Pending Inventory Costs (Live)</option>
          </Form.Select>
        </Col>

        {exportType === 'quarterly_sales' && (
          <>
            <Col md={2}>
              <Form.Label>Start</Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Col>

            <Col md={2}>
              <Form.Label>End</Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Col>
          </>
        )}

        {exportType === 'top_inventory_cost' && (
          <Col md={3}>
            <Form.Select
              value={topLimit}
              onChange={(e) => setTopLimit(Number(e.target.value))}
            >
              <option value={25}>Top 25</option>
              <option value={50}>Top 50</option>
              <option value={100}>Top 100</option>
            </Form.Select>
          </Col>
        )}

        <Col md={3}>
          <Button variant="success" onClick={handleExport} disabled={loadingReport}>
            {loadingReport ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Loading…
              </>
            ) : (
              'Run'
            )}
          </Button>
        </Col>
      </Row>

      {/* ================= TOTAL INVENTORY COST ================= */}
      {totalInventoryCost !== null && (
        <Card className="mt-4">
          <Card.Body>
            <h4>Total Inventory Cost</h4>
            <h2 className="mt-2">${money(totalInventoryCost)}</h2>
          </Card.Body>
        </Card>
      )}

      {/* ================= QUICK BUY REPORT ================= */}
      {quickBuyReport && (
        <Card className="mt-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h4 className="mb-1">Quick Buy Value Report</h4>
                <div className="text-muted">
                  Active products currently inside your Quick Buy list.
                </div>
              </div>

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={fetchQuickBuyReport}
                disabled={loadingReport}
              >
                Refresh
              </Button>
            </div>

            <Row className="g-3">
              <Col md={3}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted">Quick Buy SKUs</div>
                    <h3 className="mb-0">{toNumber(quickBuyReport.total_skus)}</h3>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={3}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted">Total Units</div>
                    <h3 className="mb-0">{toNumber(quickBuyReport.total_units)}</h3>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={3}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted">Total COGS</div>
                    <h3 className="mb-0">${money(quickBuyReport.total_cogs)}</h3>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={3}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted">Value If Sold</div>
                    <h3 className="mb-0 text-success">
                      ${money(quickBuyReport.total_value_if_sold)}
                    </h3>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={12}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted">Potential Gross Profit</div>
                    <h3
                      className={
                        toNumber(quickBuyReport.gross_profit_if_sold) >= 0
                          ? 'mb-0 text-success'
                          : 'mb-0 text-danger'
                      }
                    >
                      ${money(quickBuyReport.gross_profit_if_sold)}
                    </h3>

                    <div className="text-muted mt-2">
                      Formula: current product price × stock, minus COGS × stock.
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* ================= PENDING INVENTORY COSTS REPORT ================= */}
      {pendingInventoryCostsReport && (
        <Card className="mt-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h4 className="mb-1">Pending Inventory Costs</h4>
                <div className="text-muted">
                  FIFO/cost batches that are still pending and not released into active inventory.
                </div>
              </div>

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={fetchPendingInventoryCosts}
                disabled={loadingReport}
              >
                Refresh
              </Button>
            </div>

            <Row className="g-3">
              <Col md={3}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted">Pending SKUs</div>
                    <h3 className="mb-0">
                      {toNumber(pendingInventoryCostsReport.total_skus)}
                    </h3>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={3}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted">Pending Units</div>
                    <h3 className="mb-0">
                      {toNumber(pendingInventoryCostsReport.total_units)}
                    </h3>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={3}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted">Pending COGS</div>
                    <h3 className="mb-0">
                      ${money(pendingInventoryCostsReport.total_cogs)}
                    </h3>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={3}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted">Value If Sold</div>
                    <h3 className="mb-0 text-success">
                      ${money(pendingInventoryCostsReport.total_value_if_sold)}
                    </h3>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={12}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted">Potential Gross Profit</div>
                    <h3
                      className={
                        toNumber(pendingInventoryCostsReport.gross_profit_if_sold) >= 0
                          ? 'mb-0 text-success'
                          : 'mb-0 text-danger'
                      }
                    >
                      ${money(pendingInventoryCostsReport.gross_profit_if_sold)}
                    </h3>

                    <div className="text-muted mt-2">
                      Formula: current product price × pending quantity, minus pending COGS.
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {(pendingInventoryCostsReport.items || []).length > 0 && (
              <div className="mt-4">
                <h5>Pending Inventory Items</h5>

                <div style={{ maxHeight: 450, overflowY: 'auto' }}>
                  <table className="table table-sm table-striped mt-2">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Item</th>
                        <th>Pending Qty</th>
                        <th>Unit Cost</th>
                        <th>Price</th>
                        <th>Pending COGS</th>
                        <th>Value If Sold</th>
                        <th>Gross Profit</th>
                      </tr>
                    </thead>

                    <tbody>
                      {(pendingInventoryCostsReport.items || []).map((row) => (
                        <tr key={`${row.id}-${row.sku}`}>
                          <td>{row.sku}</td>
                          <td>{row.name}</td>
                          <td>{toNumber(row.remaining_qty)}</td>
                          <td>${money(row.purchase_cost)}</td>
                          <td>${money(row.price)}</td>
                          <td>${money(row.pending_cogs)}</td>
                          <td>${money(row.value_if_sold)}</td>
                          <td
                            className={
                              toNumber(row.gross_profit_if_sold) >= 0
                                ? 'text-success'
                                : 'text-danger'
                            }
                          >
                            ${money(row.gross_profit_if_sold)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* ================= LIVE TOP INVENTORY TABLE ================= */}
      {inventoryReport && (
        <div className="mt-4">
          <h5>
            Top {inventoryReport.count} SKUs — Total Cost: $
            {money(inventoryReport.top_total_cost)} ({inventoryReport.percent_of_inventory}% of
            inventory)
          </h5>

          <div style={{ maxHeight: 450, overflowY: 'auto' }}>
            <table className="table table-sm table-striped mt-2">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Unit Cost</th>
                  <th>Total Cost</th>
                </tr>
              </thead>

              <tbody>
                {(inventoryReport.items || []).map((row) => (
                  <tr key={row.sku}>
                    <td>{row.sku}</td>
                    <td>{row.name}</td>
                    <td>{toNumber(row.quantity)}</td>
                    <td>${money(row.unit_cost)}</td>
                    <td>${money(row.total_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Container>
  );
};

export default BulkUploader;